import { useState } from 'react'
import { Alert, Box, Button, Link, Stack } from '@mui/material'
import { closeAsDuplicate, closeAsRelated, closeDuplicates, closeRelated } from './api.js'
import { bannerSegments, relatedBanners } from './relatedContacts.js'
import QuietCheckGate from './QuietCheckGate.jsx'

// Related contacts (2026-10-20; ui/src/relatedContacts.js,
// docs/pipeline-behaviors.md "Related contacts", docs/contact-view.md): the
// banners above the AI Suggestion. Agents close the copies and same-issue
// contacts FIRST, then reply on the primary (close first, 2026-10-20). Every
// button closes only on this click; nothing closes on its own.
// Every close button names the contact(s) it closes (2026-10-20, button
// targets), the same label the banner's sentence names (closeTarget() in
// relatedContacts.js):
//   duplicate  "Duplicate of #… Handle it there." Open #…, Close This Contact
//              (#…) As Duplicate
//   secondary  "Same issue as #…, which has the reply that covers both. …"
//              Open #…, Close This Contact (#…) As Related
//   primary    "n duplicate(s) … Select Close #…, #… to close them now."
//              Close #…, #…
//   sameIssue  "Customer also wrote about this same issue on #… …"
//              Close #…
//   handled    the group's primary only (2026-10-21). Covered: "Same
//              request as #…, closed … by …. Our reply: …. If that reply
//              still answers this, select Close #a, #b – No Reply Needed."
//              That button closes the whole open group (the panel's Close –
//              No Reply Needed with closeAnsweredGroup), and Open #…. Not
//              covered: "We answered a similar request on #… before ….
//              This needs a new reply." Open #… only.
//   different  "Customer also has an open contact about …: #…" (a link)
// Each contact number in a banner, and every Open button, opens that
// contact in a CXHub contact tab through onOpenConversation (2026-10-21: no
// banner opens a browser tab). A contact the viewer may not open is plain
// text with no button.
export default function RelatedContactsBanners({ related, conversationId, onOpenConversation, onCloseNoReply, onChanged, closeNoReplyDisabled = false, closeNoReplyWaiting = false }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const banners = relatedBanners(related)
  if (!banners.length) return null

  async function run(fn) {
    setBusy(true); setError(null)
    try {
      await fn()
      onChanged?.()
    } catch (e) {
      setError(e.body?.message ?? e.message)
    } finally {
      setBusy(false)
    }
  }

  function renderText(banner) {
    return bannerSegments(banner).map((seg, i) => {
      if (typeof seg === 'string') return <span key={i}>{seg}</span>
      return onOpenConversation
        ? <Link key={i} component="button" type="button" onClick={() => onOpenConversation(seg.id)} sx={{ verticalAlign: 'baseline' }}>#{seg.convNumber}</Link>
        : <span key={i}>#{seg.convNumber}</span>
    })
  }

  return (
    <Box data-testid="related-contacts-banners" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
      {banners.map((b) => (
        <Alert key={`${b.kind}-${b.id ?? b.primaryId ?? 'self'}`} severity={b.severity} data-testid={`related-banner-${b.kind}`} sx={{ py: 0.25 }}>
          {renderText(b)}
          {(b.kind === 'duplicate' || b.kind === 'secondary') && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {onOpenConversation && <Button size="small" variant="outlined" onClick={() => onOpenConversation(b.primaryId)}>Open #{b.primaryConvNumber ?? '?'}</Button>}
              {b.kind === 'duplicate'
                ? <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => run(() => closeAsDuplicate(conversationId))}>{b.closeLabel}</Button>
                : <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => run(() => closeAsRelated(conversationId))}>{b.closeLabel}</Button>}
            </Stack>
          )}
          {b.kind === 'primary' && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => run(() => closeDuplicates(conversationId))}>{b.closeLabel}</Button>
            </Stack>
          )}
          {b.kind === 'sameIssue' && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => run(() => closeRelated(conversationId))}>{b.closeLabel}</Button>
            </Stack>
          )}
          {b.kind === 'handled' && ((onCloseNoReply && b.offerClose) || (b.canOpen && onOpenConversation)) && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {onCloseNoReply && b.offerClose && <QuietCheckGate waiting={closeNoReplyWaiting}><Button size="small" variant="outlined" disabled={busy || closeNoReplyDisabled} onClick={() => onCloseNoReply(b.id)}>{b.closeLabel}</Button></QuietCheckGate>}
              {b.canOpen && onOpenConversation && (
                <Button size="small" variant="outlined" data-testid="related-open-handled" onClick={() => onOpenConversation(b.id)}>{b.openLabel}</Button>
              )}
            </Stack>
          )}
        </Alert>
      ))}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0 }}>{error}</Alert>}
    </Box>
  )
}
